/**
 * ClickReel SDK - Shadow DOM Module
 *
 * Creates an isolated Shadow DOM container to prevent CSS bleeding
 * and ensure z-index isolation. Target: CLS Score = 0
 */

import { CONTAINER_ID, Z_INDEX } from './types';

// =============================================================================
// Shadow DOM Host Element
// =============================================================================

/**
 * Custom element that hosts the ClickReel Shadow DOM
 */
export class ClickReelHost extends HTMLElement {
  private shadowContainer: ShadowRoot;
  private styleSheet: CSSStyleSheet | null = null;

  constructor() {
    super();
    // Create closed shadow root for complete isolation
    this.shadowContainer = this.attachShadow({ mode: 'closed' });
    this.applyHostStyles();
  }

  /**
   * Get the shadow root for rendering content
   */
  get container(): ShadowRoot {
    return this.shadowContainer;
  }

  /**
   * Apply styles to the shadow root
   */
  private applyHostStyles(): void {
    // Base reset styles for Shadow DOM
    const styleElement = document.createElement('style');
    styleElement.textContent = getBaseStyles();
    this.shadowContainer.appendChild(styleElement);
  }

  /**
   * Add additional styles to the shadow root
   */
  addStyles(css: string): void {
    const styleElement = document.createElement('style');
    styleElement.textContent = css;
    this.shadowContainer.appendChild(styleElement);
  }

  /**
   * Clear all content from shadow root except styles
   */
  clearContent(): void {
    const children = Array.from(this.shadowContainer.children);
    children.forEach(child => {
      if (child.tagName !== 'STYLE') {
        child.remove();
      }
    });
  }
}

// =============================================================================
// Shadow DOM Manager
// =============================================================================

let hostElement: ClickReelHost | null = null;

/**
 * Initialize the Shadow DOM container
 * Ensures only one instance exists
 */
export function initShadowDOM(): ClickReelHost {
  // Check if already initialized
  if (hostElement && document.body.contains(hostElement)) {
    return hostElement;
  }

  // Define custom element if not already defined
  if (!customElements.get('clickreel-host')) {
    customElements.define('clickreel-host', ClickReelHost);
  }

  // Create and append host element
  hostElement = document.createElement('clickreel-host') as ClickReelHost;
  hostElement.id = CONTAINER_ID;

  // Apply host element styles (position fixed, full viewport, no pointer events by default)
  hostElement.style.cssText = `
    position: fixed !important;
    top: 0 !important;
    left: 0 !important;
    width: 100% !important;
    height: 100% !important;
    pointer-events: none !important;
    z-index: ${Z_INDEX} !important;
    contain: layout style !important;
  `;

  // Insert at the end of body for maximum z-index priority
  document.body.appendChild(hostElement);

  return hostElement;
}

/**
 * Get the Shadow DOM container
 */
export function getShadowContainer(): ShadowRoot | null {
  return hostElement?.container ?? null;
}

/**
 * Get the host element
 */
export function getHostElement(): ClickReelHost | null {
  return hostElement;
}

/**
 * Destroy the Shadow DOM container
 */
export function destroyShadowDOM(): void {
  if (hostElement) {
    hostElement.remove();
    hostElement = null;
  }
}

// =============================================================================
// Base Styles for Shadow DOM
// =============================================================================

/**
 * Returns base CSS reset and common styles for the Shadow DOM
 * These styles are completely isolated from the host page
 */
function getBaseStyles(): string {
  return `
    /* CSS Reset for Shadow DOM */
    *, *::before, *::after {
      box-sizing: border-box;
      margin: 0;
      padding: 0;
    }

    /* Ensure pointer events work on interactive elements */
    button, a, [role="button"], .interactive {
      pointer-events: auto;
    }

    /* Base button reset */
    button {
      font-family: inherit;
      font-size: inherit;
      line-height: inherit;
      cursor: pointer;
      border: none;
      background: transparent;
    }

    /* Animations */
    @keyframes clickreel-fade-in {
      from {
        opacity: 0;
        transform: scale(0.8);
      }
      to {
        opacity: 1;
        transform: scale(1);
      }
    }

    @keyframes clickreel-fade-out {
      from {
        opacity: 1;
        transform: scale(1);
      }
      to {
        opacity: 0;
        transform: scale(0.8);
      }
    }

    @keyframes clickreel-slide-up {
      from {
        opacity: 0;
        transform: translateY(20px);
      }
      to {
        opacity: 1;
        transform: translateY(0);
      }
    }

    @keyframes clickreel-pulse {
      0%, 100% {
        transform: scale(1);
      }
      50% {
        transform: scale(1.05);
      }
    }

    @keyframes clickreel-spin {
      from {
        transform: rotate(0deg);
      }
      to {
        transform: rotate(360deg);
      }
    }

    /* Entrance animations */
    @keyframes clickreel-slide-from-right {
      from { opacity: 0; transform: translateX(80px); }
      to { opacity: 1; transform: translateX(0); }
    }

    @keyframes clickreel-slide-from-left {
      from { opacity: 0; transform: translateX(-80px); }
      to { opacity: 1; transform: translateX(0); }
    }

    @keyframes clickreel-bounce-in {
      0% { opacity: 0; transform: scale(0.3); }
      50% { opacity: 1; transform: scale(1.08); }
      70% { transform: scale(0.95); }
      100% { opacity: 1; transform: scale(1); }
    }

    @keyframes clickreel-grow-in {
      0% { opacity: 1; transform: scale(1.5); }
      100% { opacity: 1; transform: scale(1); }
    }

    @keyframes clickreel-pulse-in {
      0% { opacity: 0; transform: scale(1); }
      30% { opacity: 1; transform: scale(1.15); }
      50% { opacity: 1; transform: scale(0.95); }
      70% { opacity: 1; transform: scale(1.05); }
      100% { opacity: 1; transform: scale(1); }
    }

    /* Accessibility */
    .visually-hidden {
      position: absolute;
      width: 1px;
      height: 1px;
      padding: 0;
      margin: -1px;
      overflow: hidden;
      clip: rect(0, 0, 0, 0);
      white-space: nowrap;
      border: 0;
    }

    /* Focus styles */
    :focus-visible {
      outline: 2px solid currentColor;
      outline-offset: 2px;
    }

    /* Reduced motion */
    @media (prefers-reduced-motion: reduce) {
      *, *::before, *::after {
        animation-duration: 0.01ms !important;
        animation-iteration-count: 1 !important;
        transition-duration: 0.01ms !important;
      }
    }
  `;
}
